import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function login(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result;
  }

  async function sendLoginLink(email) {
    const actionCodeSettings = {
      url: window.location.origin + '/login',
      handleCodeInApp: true,
    };
    
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save email to localStorage to complete sign-in
    window.localStorage.setItem('emailForSignIn', email);
  }

  async function completeLoginWithLink(email, emailLink) {
    const result = await signInWithEmailLink(auth, email, emailLink);
    window.localStorage.removeItem('emailForSignIn');
    return result;
  }

  function checkIfSignInLink() {
    return isSignInWithEmailLink(auth, window.location.href);
  }

  async function logout() {
    return signOut(auth);
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Fetch user profile from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    sendLoginLink,
    completeLoginWithLink,
    checkIfSignInLink,
    logout,
    resetPassword,
    isAdmin: ['admin', 'president', 'vp_projects', 'vp_chapters', 'treasurer'].includes(userProfile?.role),
    isLeadership: ['admin', 'president', 'vp_projects', 'vp_chapters', 'treasurer'].includes(userProfile?.role)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}